import React, { useEffect, useState } from "react";
import { Edit, Trash2, Plus, Building, User, Ruler, Users, Filter } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import "./UnitsOfMeasureList.css";

const url = process.env.REACT_APP_BACKEND_URL;

const UnitsOfMeasureList = () => {
  const { user } = useAuth();
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showAllCompanies, setShowAllCompanies] = useState(false);
  const navigate = useNavigate();

  // Fetch companies for system admins
  useEffect(() => {
    if (user?.userType === 'system' && (user.role === 'developer' || user.role === 'admin')) {
      fetchCompanies();
    }
  }, [user]);

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${url}/api/companies`);
      if (res.data.success) {
        setCompanies(res.data.data || []);
      }
    } catch (error) {
      // Silent fail
    }
  };

  const fetchUOMs = async () => {
    try {
      setLoading(true);
      
      let urlPath = `${url}/api/uom`;
      if (showAllCompanies && user?.userType === 'system') {
        urlPath = `${url}/api/uom/all`;
      }
      
      const res = await axios.get(urlPath, {
        params: selectedCompany ? { company_id: selectedCompany } : {}
      });
      
      if (res.data.success) {
        setUoms(res.data.data || []);
      } else {
        toast.error(res.data.message || "Failed to fetch units of measure");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch units of measure");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUOMs();
  }, [selectedCompany, showAllCompanies]);

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        const res = await axios.delete(`${url}/api/uom/${id}`);
        
        if (res.data.success) {
          toast.success(`"${name}" deleted successfully`);
          fetchUOMs();
        } else {
          toast.error(res.data.message || "Failed to delete unit");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to delete unit");
      }
    }
  };

  const filteredUOMs = uoms.filter(
    (uom) =>
      uom.uom_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      uom.uom_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (uom.company_id?.company_name && uom.company_id.company_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading">Loading units of measure...</div>
      </div>
    );
  }

  return (
    <div className="uom-container">
      <div className="header">
        <div>
          <h2>Units of Measure</h2>
          <p className="user-role-info">
            {user?.userType === 'system' 
              ? <><Users size={14} /> System Administrator</>
              : <><Building size={14} /> Company User</>}
          </p>
        </div>
        <button onClick={() => navigate("/AddUnitsOfMeasure")} className="add-button">
          <Plus size={16} /> Add Unit
        </button>
      </div>

      {/* Company Filter for System Admins */}
      {user?.userType === 'system' && (user.role === 'developer' || user.role === 'admin') && (
        <div className="filter-section">
          <div className="filter-controls">
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="company-filter"
            >
              <option value="">All Companies</option>
              {companies.map((company) => (
                <option key={company._id} value={company._id}>
                  {company.company_name} ({company.company_code})
                </option>
              ))}
            </select>
            
            <button 
              onClick={() => setShowAllCompanies(!showAllCompanies)}
              className={`view-toggle-btn ${showAllCompanies ? 'active' : ''}`}
            >
              <Filter size={14} />
              {showAllCompanies ? "Show Filtered" : "Show All Companies"}
            </button>
          </div>
        </div>
      )}

      <div className="stats-bar">
        <span>Total: {uoms.length} units</span>
        <span>User: {user?.name || user?.Usercomp_sure_name || "Unknown"}</span>
        {!selectedCompany && uoms[0]?.company_id && (
          <span>Company: {uoms[0].company_id.company_name}</span>
        )}
      </div>

      {/* Search Box */}
      <div className="search-container">
        <input
          type="text"
          placeholder="Search units by name, type, or company..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <button className="clear-search-btn" onClick={() => setSearchTerm("")}>
            ×
          </button>
        )}
      </div>

      <table className="uom-table">
        <thead>
          <tr>
            {showAllCompanies || selectedCompany === "" ? <th>Company</th> : null}
            <th>Unit Name</th>
            <th>Type</th>
            <th>Created By</th>
            <th>Created At</th>
            <th style={{ textAlign: "center" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredUOMs.length === 0 ? (
            <tr>
              <td colSpan={showAllCompanies || selectedCompany === "" ? "6" : "5"} className="no-data">
                No units of measure found. {searchTerm && `No results for "${searchTerm}"`}
              </td>
            </tr>
          ) : (
            filteredUOMs.map((uom) => (
              <tr key={uom._id}>
                {(showAllCompanies || selectedCompany === "") && (
                  <td>
                    <div className="company-info">
                      <Building size={12} />
                      <span>{uom.company_id?.company_name || "Unknown"}</span>
                      <small className="company-code">{uom.company_id?.company_code}</small>
                    </div>
                  </td>
                )}
                <td>
                  <div className="uom-name">
                    <Ruler size={14} className="inline mr-2" />
                    <strong>{uom.uom_name}</strong>
                  </div>
                </td>
                <td>
                  <span className="type-badge">{uom.uom_type}</span>
                </td>
                <td>
                  <div className="user-info">
                    <User size={12} />
                    <div>
                      <span>{uom.created_by?.name || "System"}</span>
                      <small className="user-email">{uom.created_by?.email}</small>
                    </div>
                  </div>
                </td>
                <td>
                  {new Date(uom.created_at).toLocaleDateString()}
                </td>
                <td className="actions">
                  <button 
                    onClick={() => navigate(`/uom/edit/${uom._id}`)} 
                    className="edit-btn"
                    title="Edit"
                  >
                    <Edit size={14} />
                  </button>
                  <button 
                    onClick={() => handleDelete(uom._id, uom.uom_name)} 
                    className="delete-btn"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default UnitsOfMeasureList;